import { useEffect, useMemo, useState } from 'react';

// Visual cron builder. Returns a standard 5-field cron string via onChange.
// 90% of real schedules fit one of six modes; the 10% that don't drop into
// the Advanced tab and edit the raw expression with the preview validating
// in real time.

type Mode = 'every_minutes' | 'every_hours' | 'daily' | 'weekly' | 'monthly' | 'advanced';

const MODE_OPTIONS: { value: Mode; label: string }[] = [
  { value: 'every_minutes', label: 'Every X minutes' },
  { value: 'every_hours', label: 'Every X hours' },
  { value: 'daily', label: 'Every day' },
  { value: 'weekly', label: 'Every week' },
  { value: 'monthly', label: 'Every month' },
  { value: 'advanced', label: 'Custom (cron)' },
];

const DAYS = [
  { value: 1, short: 'Mon' },
  { value: 2, short: 'Tue' },
  { value: 3, short: 'Wed' },
  { value: 4, short: 'Thu' },
  { value: 5, short: 'Fri' },
  { value: 6, short: 'Sat' },
  { value: 0, short: 'Sun' },
];

type Props = {
  value: string;
  onChange: (cronExpr: string) => void;
};

export function CronBuilder({ value, onChange }: Props) {
  const initial = useMemo(() => detectMode(value), [value]);

  const [mode, setMode] = useState<Mode>(initial.mode);
  const [everyMinutes, setEveryMinutes] = useState(initial.everyMinutes ?? 15);
  const [everyHours, setEveryHours] = useState(initial.everyHours ?? 1);
  const [hour, setHour] = useState(initial.hour ?? 9);
  const [minute, setMinute] = useState(initial.minute ?? 0);
  const [days, setDays] = useState<number[]>(initial.days ?? [1]);
  const [dayOfMonth, setDayOfMonth] = useState(initial.dayOfMonth ?? 1);
  const [advanced, setAdvanced] = useState(value);

  useEffect(() => {
    // When the parent's value changes from outside (e.g. preset selected),
    // resync the builder.
    const m = detectMode(value);
    setMode(m.mode);
    if (m.everyMinutes !== undefined) setEveryMinutes(m.everyMinutes);
    if (m.everyHours !== undefined) setEveryHours(m.everyHours);
    if (m.hour !== undefined) setHour(m.hour);
    if (m.minute !== undefined) setMinute(m.minute);
    if (m.days) setDays(m.days);
    if (m.dayOfMonth !== undefined) setDayOfMonth(m.dayOfMonth);
    if (m.mode === 'advanced') setAdvanced(value);
  }, [value]);

  // Emit a fresh cron whenever any builder input changes.
  useEffect(() => {
    if (mode === 'advanced') {
      onChange(advanced);
      return;
    }
    const next = buildCron(mode, { everyMinutes, everyHours, hour, minute, days, dayOfMonth });
    onChange(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, everyMinutes, everyHours, hour, minute, days, dayOfMonth, advanced]);

  return (
    <div className="space-y-3">
      <div>
        <label className="block text-xs font-medium text-ink-secondary">Frequency</label>
        <select
          className="mt-1 block w-full rounded-md border border-line bg-surface-white px-2 py-1.5 text-sm text-ink-primary focus:border-bit2me focus:outline-none focus:ring-1 focus:ring-bit2me"
          value={mode}
          onChange={(e) => setMode(e.target.value as Mode)}
        >
          {MODE_OPTIONS.map((m) => (
            <option key={m.value} value={m.value}>
              {m.label}
            </option>
          ))}
        </select>
      </div>

      {mode === 'every_minutes' && (
        <Field label="Run every">
          <select
            value={everyMinutes}
            onChange={(e) => setEveryMinutes(Number(e.target.value))}
            className={selectClass}
          >
            {[5, 10, 15, 20, 30].map((n) => (
              <option key={n} value={n}>
                {n} minutes
              </option>
            ))}
          </select>
        </Field>
      )}

      {mode === 'every_hours' && (
        <div className="grid grid-cols-2 gap-3">
          <Field label="Run every">
            <select
              value={everyHours}
              onChange={(e) => setEveryHours(Number(e.target.value))}
              className={selectClass}
            >
              {[1, 2, 3, 4, 6, 8, 12].map((n) => (
                <option key={n} value={n}>
                  {n} hour{n === 1 ? '' : 's'}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Starting at minute">
            <MinuteSelect value={minute} onChange={setMinute} />
          </Field>
        </div>
      )}

      {mode === 'daily' && (
        <div className="grid grid-cols-2 gap-3">
          <Field label="Hour">
            <HourSelect value={hour} onChange={setHour} />
          </Field>
          <Field label="Minute">
            <MinuteSelect value={minute} onChange={setMinute} />
          </Field>
        </div>
      )}

      {mode === 'weekly' && (
        <div className="space-y-2">
          <Field label="Days">
            <div className="flex flex-wrap gap-1.5">
              {DAYS.map((d) => {
                const active = days.includes(d.value);
                return (
                  <button
                    key={d.value}
                    type="button"
                    onClick={() => setDays(toggle(days, d.value))}
                    className={`rounded-md border px-2.5 py-1 font-mono text-[10px] uppercase transition-colors ${
                      active
                        ? 'border-bit2me bg-bit2me text-white'
                        : 'border-line bg-surface-white text-ink-secondary hover:border-ink-secondary'
                    }`}
                  >
                    {d.short}
                  </button>
                );
              })}
            </div>
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Hour">
              <HourSelect value={hour} onChange={setHour} />
            </Field>
            <Field label="Minute">
              <MinuteSelect value={minute} onChange={setMinute} />
            </Field>
          </div>
        </div>
      )}

      {mode === 'monthly' && (
        <div className="grid grid-cols-3 gap-3">
          <Field label="Day of month">
            <select
              value={dayOfMonth}
              onChange={(e) => setDayOfMonth(Number(e.target.value))}
              className={selectClass}
            >
              {Array.from({ length: 31 }, (_, i) => i + 1).map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Hour">
            <HourSelect value={hour} onChange={setHour} />
          </Field>
          <Field label="Minute">
            <MinuteSelect value={minute} onChange={setMinute} />
          </Field>
        </div>
      )}

      {mode === 'advanced' && (
        <Field label="Cron expression (5 fields)">
          <input
            type="text"
            value={advanced}
            onChange={(e) => setAdvanced(e.target.value)}
            placeholder="0 9 * * 1-5"
            className="block w-full rounded-md border border-line bg-surface-white px-2 py-1.5 font-mono text-xs text-ink-primary focus:border-bit2me focus:outline-none focus:ring-1 focus:ring-bit2me"
          />
        </Field>
      )}

      <p className="font-mono text-[11px] text-ink-tertiary">
        Resulting expression:{' '}
        <code className="rounded bg-surface-soft px-1 py-0.5 text-ink-secondary">
          {mode === 'advanced'
            ? advanced
            : buildCron(mode, { everyMinutes, everyHours, hour, minute, days, dayOfMonth })}
        </code>
      </p>
    </div>
  );
}

const selectClass =
  'block w-full rounded-md border border-line bg-surface-white px-2 py-1.5 text-sm text-ink-primary focus:border-bit2me focus:outline-none focus:ring-1 focus:ring-bit2me';

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-xs font-medium text-ink-secondary">{label}</label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function HourSelect({ value, onChange }: { value: number; onChange: (n: number) => void }) {
  return (
    <select value={value} onChange={(e) => onChange(Number(e.target.value))} className={selectClass}>
      {Array.from({ length: 24 }, (_, i) => i).map((n) => (
        <option key={n} value={n}>
          {String(n).padStart(2, '0')}h
        </option>
      ))}
    </select>
  );
}

function MinuteSelect({ value, onChange }: { value: number; onChange: (n: number) => void }) {
  return (
    <select value={value} onChange={(e) => onChange(Number(e.target.value))} className={selectClass}>
      {[0, 5, 10, 15, 20, 30, 45].map((n) => (
        <option key={n} value={n}>
          :{String(n).padStart(2, '0')}
        </option>
      ))}
    </select>
  );
}

function toggle(list: number[], v: number): number[] {
  return list.includes(v) ? list.filter((x) => x !== v) : [...list, v].sort();
}

type BuildArgs = {
  everyMinutes: number;
  everyHours: number;
  hour: number;
  minute: number;
  days: number[];
  dayOfMonth: number;
};

function buildCron(mode: Mode, a: BuildArgs): string {
  switch (mode) {
    case 'every_minutes':
      return `*/${a.everyMinutes} * * * *`;
    case 'every_hours':
      return `${a.minute} */${a.everyHours} * * *`;
    case 'daily':
      return `${a.minute} ${a.hour} * * *`;
    case 'weekly': {
      const days = a.days.length === 0 ? '*' : a.days.join(',');
      return `${a.minute} ${a.hour} * * ${days}`;
    }
    case 'monthly':
      return `${a.minute} ${a.hour} ${a.dayOfMonth} * *`;
    case 'advanced':
      return ''; // handled by caller's advanced input
  }
}

// detectMode tries to reverse-engineer a builder mode from an existing cron
// string so reopening an already-saved task drops the user back into the
// visual editor when possible. Anything we can't classify lands in
// Advanced.
function detectMode(cron: string): {
  mode: Mode;
  everyMinutes?: number;
  everyHours?: number;
  hour?: number;
  minute?: number;
  days?: number[];
  dayOfMonth?: number;
} {
  const parts = cron.trim().split(/\s+/);
  if (parts.length !== 5) return { mode: 'advanced' };
  const [m, h, dom, mon, dow] = parts;

  // every X minutes — only when hour/day/month are wildcards
  const everyMin = /^\*\/(\d+)$/.exec(m);
  if (everyMin && h === '*' && dom === '*' && mon === '*' && dow === '*') {
    return { mode: 'every_minutes', everyMinutes: parseInt(everyMin[1], 10) };
  }
  // every X hours
  const everyHr = /^\*\/(\d+)$/.exec(h);
  const fixedMin = /^\d+$/.exec(m);
  if (everyHr && fixedMin && dom === '*' && mon === '*' && dow === '*') {
    return {
      mode: 'every_hours',
      everyHours: parseInt(everyHr[1], 10),
      minute: parseInt(fixedMin[0], 10),
    };
  }
  // weekly — minute + hour fixed, dom + mon wild, dow non-wild
  if (fixedMin && /^\d+$/.test(h) && dom === '*' && mon === '*' && dow !== '*') {
    const ds = parseDays(dow);
    if (ds) {
      return {
        mode: 'weekly',
        minute: parseInt(fixedMin[0], 10),
        hour: parseInt(h, 10),
        days: ds,
      };
    }
  }
  // monthly — minute + hour + dom fixed, mon + dow wild
  if (fixedMin && /^\d+$/.test(h) && /^\d+$/.test(dom) && mon === '*' && dow === '*') {
    return {
      mode: 'monthly',
      minute: parseInt(fixedMin[0], 10),
      hour: parseInt(h, 10),
      dayOfMonth: parseInt(dom, 10),
    };
  }
  // daily — minute + hour fixed, everything else wild
  if (fixedMin && /^\d+$/.test(h) && dom === '*' && mon === '*' && dow === '*') {
    return { mode: 'daily', minute: parseInt(fixedMin[0], 10), hour: parseInt(h, 10) };
  }
  return { mode: 'advanced' };
}

function parseDays(s: string): number[] | null {
  // Accept either a single number, a comma list, or a hyphen range like 1-5.
  if (/^\d$/.test(s)) return [parseInt(s, 10)];
  if (/^\d(,\d)*$/.test(s)) return s.split(',').map((x) => parseInt(x, 10));
  const range = /^(\d)-(\d)$/.exec(s);
  if (range) {
    const out: number[] = [];
    for (let i = parseInt(range[1], 10); i <= parseInt(range[2], 10); i++) out.push(i);
    return out;
  }
  return null;
}
